import { DvGridComponent, DvToolbarAction } from '@devinco/ui/grid';
import {
  Component,
  OnInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import RouteUtils from '@core/utils/route.utils';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { DamageReportStatus } from '@shared/constants/damage-report-status';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { DirectivesModule } from '@shared/directives/directives.module';
import { AgGridModule } from 'ag-grid-angular';
import {
  IRowNode,
  ModelUpdatedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { DamageReportsGridService } from './damage-reports-grid.service';
import { DamageReportDto } from './interfaces';

@Component({
  imports: [DvGridComponent, AgGridModule, DirectivesModule],
  selector: 'app-damage-reports-grid',
  templateUrl: './damage-reports-grid.component.html',
  styles: `
    :host {
      height: 100%;
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DamageReportsGridService, GridOptionsService],
})
export class DamageReportsGridComponent implements OnInit {
  private readonly gridService = inject(DamageReportsGridService);
  private readonly gridOptionsService = inject(GridOptionsService);
  private readonly activatedRoute = inject(ActivatedRoute);

  gridOptions: GridOptions;
  actions = signal<DvToolbarAction[]>([]);

  ngOnInit(): void {
    this.gridOptions = this.gridOptionsService.serverSide(
      this.gridService.getColDefs(),
      Entity.DamageReport
    );
    this.actions.set(this.gridService.getGridActions());
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    const selectedNode = api.getSelectedNodes()[0] as
      | IRowNode<DamageReportDto>
      | undefined;
    const isDraft =
      selectedNode?.data?.DamageReportStatus === DamageReportStatus.Draft;

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'delete') {
          return { ...action, disabled: !selectedNode || !isDraft };
        }
        if (action.icon === 'export_pdf') {
          return { ...action, disabled: !selectedNode?.data?.DamageReportID };
        }
        return action;
      })
    );
  }

  /** Selects the first row when the entity open in the sidesheet is no
   * longer present in the grid after a refresh (e.g. reverted to Draft). */
  onModelUpdated({ api }: ModelUpdatedEvent): void {
    const entityId = RouteUtils.getEntityId(this.activatedRoute.firstChild);
    if (!entityId || entityId === NEW_ENTITY_ROUTE_PARAM) return;
    if (api.getRowNode(entityId)) return;

    const firstNode = api.getDisplayedRowAtIndex(0);
    if (firstNode?.id) {
      firstNode.setSelected(true, true);
    }
  }
}
